import React, { useState, useEffect, useMemo, useContext } from "react";
import { Link, useParams } from "react-router-dom";

import axios from "axios";
import { DiscussionEmbed } from 'disqus-react';
import config from "../config";
import { Helmet } from 'react-helmet';
import translations from "../translations/blog";
import headerTranslations from "../components/header/header-translations";
import AlternatesContext from "../components/header/AlternatesContext";
import ArticlesList from "../components/articlesList/ArticlesList";
import { CheckIcon, PhoneIcon } from "../components/articlesList/BlogIcons";
import {
  blogPath, quotePath, pickTranslation, formatDate, readingMinutes, cloudinary, cloudinarySrcSet, frenchSpacing,
} from "../components/articlesList/articleUtils";
import ui from "../components/articlesList/ArticlesList.module.css";
import styles from "./ArticleDetail.module.css";

const HERO_IMAGE = "c_fill,g_auto,ar_2.1";
const SITE = "https://www.davoyage.com";

// Gives each <h2> an id for the table of contents and splits the body before the 4th <h2>,
// so the quote block can sit in the middle of long articles.
const prepareContent = (html, lang) => {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  const root = doc.body.querySelector(":scope > article") || doc.body;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) walker.currentNode.nodeValue = frenchSpacing(walker.currentNode.nodeValue, lang);
  const headings = [...root.querySelectorAll("h2")];
  headings.forEach((h, i) => { h.id = "section-" + (i + 1); });

  const nodes = [...root.childNodes];
  const splitIndex = headings.length >= 5 ? nodes.indexOf(headings[3]) : -1;
  const serialize = (list) => {
    const div = doc.createElement("div");
    list.forEach((node) => div.appendChild(node));
    return div.innerHTML;
  };

  return {
    toc: headings.map((h) => ({ id: h.id, label: h.textContent })),
    before: serialize(splitIndex > 0 ? nodes.slice(0, splitIndex) : nodes),
    after: splitIndex > 0 ? serialize(nodes.slice(splitIndex)) : "",
  };
};

const Article = (props) => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const setAlternates = useContext(AlternatesContext);
  const t = translations[props.lang];
  const articlePath = blogPath(props.lang);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(
          config["api-endpoint"] + "/articles/" +
          slug +
          "?lang=" +
          props.lang,
        {
          headers: {
            "x-api-key": config["x-api-key"],
          },
        }
      )
      .then((res) => {
        setArticle(pickTranslation(res.data, props.lang));
      })
      .catch((err) => {
        console.log(err);
      });
  }, [slug, props.lang]);

  useEffect(() => {
    if (!article) return;
    setAlternates(Object.fromEntries(article.translations.map((tr) => [tr.lang, blogPath(tr.lang) + tr.slug])));
    return () => setAlternates(null);
  }, [article, setAlternates]);

  const content = useMemo(() => prepareContent(article && article.content, props.lang), [article, props.lang]);

  useEffect(() => {
    if (content.toc.length < 3) return;
    const onScroll = () => {
      let current = content.toc[0].id;
      content.toc.forEach(({ id }) => {
        const heading = document.getElementById(id);
        if (heading && heading.getBoundingClientRect().top < 140) current = id;
      });
      setActiveSection(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [content]);

  if (!article) {
    return <div className={styles.page} />;
  }

  const category = t.categories[article.category];
  const url = SITE + articlePath + article.slug;
  const phone = headerTranslations[props.lang].phone;
  const scrollTo = (e, id) => {
    e.preventDefault();
    document.getElementById(id).scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className={styles.page}
      itemScope=""
      itemType="http://schema.org/BlogPosting"
    >
      <Helmet>
        <title>{article.title}</title>
        <meta name="description" content={article.metaDescription}/>
        <meta property="og:title" content={article.title}/>
        <meta property="og:type" content="article"/>
        <meta property="og:url" content={url}/>
        <meta property="og:image" content={cloudinary(article.imageLink, "c_fill,g_auto,w_1200,h_630")} />
        <meta property="og:image:alt" content={article.imageAlt || article.title}/>
        <meta property="og:description" content={article.metaDescription}/>
        <meta property="og:site_name" content="Davoyage"/>
        <meta property="article:published_time" content={article.createdAt}/>
        <link rel="canonical" href={url} />
        {article.translations.map((tr) => (
          <link key={tr.lang} rel="alternate" hrefLang={tr.lang} href={SITE + blogPath(tr.lang) + tr.slug} />
        ))}
      </Helmet>
      <meta
        itemScope=""
        itemProp="mainEntityOfPage"
        itemType="https://schema.org/WebPage"
        itemID={articlePath + article.slug}
      />

      <div className={styles.wrap}>
        <nav className={styles.crumbs} itemScope itemType="https://schema.org/BreadcrumbList">
          <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
            <Link to={articlePath} itemProp="item">
              <span itemProp="name">{t.crumb}</span>
            </Link>
            <meta itemProp="position" content="1" />
          </span>
          {category && <><span aria-hidden="true">/</span><span>{category}</span></>}
          <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
            <link itemProp="item" href={url} />
            <meta itemProp="name" content={article.title} />
            <meta itemProp="position" content="2" />
          </span>
        </nav>

        <header className={styles.head}>
          {category && <span className={ui.eyebrow}>{category}</span>}
          <h1 itemProp="headline">{frenchSpacing(article.title, props.lang)}</h1>
          {(article.excerpt || article.metaDescription) && (
            <p className={styles.lead}>{frenchSpacing(article.excerpt || article.metaDescription, props.lang)}</p>
          )}
          <div className={styles.byline}>
            <span className={styles.avatar} aria-hidden="true">D</span>
            <div>
              <strong>{t.author}</strong>
              <span className={ui.meta}>
                <time dateTime={article.createdAt}>{formatDate(article.createdAt, props.lang)}</time>
                <i className={ui.dot} />
                {readingMinutes(article.content)} {t.readTime}
              </span>
            </div>
          </div>
        </header>

        <figure className={styles.hero}>
          <img
            itemProp="image"
            src={cloudinary(article.imageLink, HERO_IMAGE + ",w_1600")}
            srcSet={cloudinarySrcSet(article.imageLink, HERO_IMAGE, [800, 1200, 1600, 2400])}
            sizes="(max-width: 1200px) 100vw, 1136px"
            alt={article.imageAlt}
          />
          {article.imageBy !== undefined && (
            <figcaption>
              {t.photoBy} <a href={article.imageBy.url} target="_blank" rel="noreferrer">{article.imageBy.name}</a> {t.photoOn} <a href={article.imageOn.url} target="_blank" rel="noreferrer">{article.imageOn.name}</a>
            </figcaption>
          )}
        </figure>

        <div className={styles.layout}>
          <div className={styles.main}>
            {article.keyFacts && article.keyFacts.length > 0 && (
              <aside className={styles.facts}>
                <span className={ui.eyebrow}>{t.facts}</span>
                {article.keyFacts.map((fact) => (
                  <div key={fact.label} className={styles.fact}>
                    <span>{fact.label}</span>
                    <b>{fact.value}</b>
                  </div>
                ))}
              </aside>
            )}

            <div itemProp="articleBody">
              <div className={styles.prose} dangerouslySetInnerHTML={{ __html: content.before }} />
              {content.after && (
                <>
                  <div className={styles.inlineCta}>
                    <div>
                      <strong>{t.ctaTitle}</strong>
                      <p>{t.ctaText}</p>
                    </div>
                    <Link className={ui.btnPrimary} to={quotePath(props.lang)}>{t.quote}</Link>
                  </div>
                  <div className={styles.prose} dangerouslySetInnerHTML={{ __html: content.after }} />
                </>
              )}
            </div>

            <meta itemProp="datePublished" content={article.createdAt.slice(0, 10)} />
            <meta itemProp="author" content="Davoyage" />
            <div
              itemProp="publisher"
              itemScope=""
              itemType="https://schema.org/Organization"
            >
              <meta itemProp="name" content="Davoyage" />
              <div
                itemProp="logo"
                itemScope=""
                itemType="https://schema.org/ImageObject"
              >
                <meta
                  itemProp="url"
                  content="https://www.davoyage.com/logo192.png"
                />
              </div>
            </div>

            <div className={styles.comments}>
              <DiscussionEmbed
                shortname='Davoyage'
                config={
                    {
                        url: url,
                        identifier: article._id,
                        title: article.title,
                        language: props.lang
                    }
                }
              />
            </div>
          </div>

          <aside className={styles.aside}>
            <div className={styles.sticky}>
              <div className={styles.book}>
                <span className={ui.eyebrow}>{t.bookEyebrow}</span>
                <p className={styles.bookTitle}>{t.bookTitle}</p>
                <ul>
                  {t.bookPoints.map((point) => <li key={point}><CheckIcon />{point}</li>)}
                </ul>
                <Link className={ui.btnPrimary} to={quotePath(props.lang)}>{t.quote}</Link>
                <a className={ui.btnLine} href="tel:+33660265336"><PhoneIcon />{phone}</a>
              </div>
              {content.toc.length >= 3 && (
                <nav className={styles.toc}>
                  <span className={ui.eyebrow}>{t.toc}</span>
                  <ol>
                    {content.toc.map(({ id, label }) => (
                      <li key={id}>
                        <a
                          href={"#" + id}
                          aria-current={activeSection === id ? "true" : undefined}
                          onClick={(e) => scrollTo(e, id)}
                        >
                          {label}
                        </a>
                      </li>
                    ))}
                  </ol>
                </nav>
              )}
            </div>
          </aside>
        </div>

        <section className={styles.related}>
          <div className={styles.relatedHead}>
            <h2>{t.related}</h2>
            <Link to={articlePath}>{t.allArticles}</Link>
          </div>
          <ArticlesList limit="4" lang={props.lang} exclude={article.slug} max="3"/>
        </section>
      </div>

      <div className={styles.mobileBar + " article-mobile-bar"}>
        <a className={ui.btnLine} href="tel:+33660265336"><PhoneIcon />{t.call}</a>
        <Link className={ui.btnPrimary} to={quotePath(props.lang)}>{t.quoteShort}</Link>
      </div>
    </div>
  );
};

export default Article;
